import { useState, useEffect, useCallback } from "react";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { rc } from "@/lib/connect";
import { MagnifyingGlass, ArrowClockwise, PaperPlaneTilt, CheckCircle, Clock, XCircle, Coins } from "@phosphor-icons/react";

const STATUS_TONE = {
  queued: "bg-slate-100 text-slate-700", sent: "bg-blue-100 text-blue-800",
  delivered: "bg-emerald-100 text-emerald-800", opened: "bg-teal-100 text-teal-800",
  clicked: "bg-teal-100 text-teal-800", bounced: "bg-red-100 text-red-800",
  complained: "bg-red-100 text-red-800", failed: "bg-red-100 text-red-800",
};

function Stat({ icon: Icon, label, value, tone = "text-foreground" }) {
  return (
    <div className="border border-border bg-card rounded-sm p-3" data-testid={`acc-stat-${label.toLowerCase().replace(/\s/g, "-")}`}>
      <div className="flex items-center gap-1.5 label-caps"><Icon size={13} weight="bold" /> {label}</div>
      <div className={`text-2xl font-black font-heading mono mt-1 ${tone}`}>{value}</div>
    </div>
  );
}

function fmt(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString(undefined, { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }); }
  catch { return iso; }
}

export default function AdminConnectComms() {
  const [summary, setSummary] = useState(null);
  const [rows, setRows] = useState(null);
  const [tenants, setTenants] = useState([]);
  const [filters, setFilters] = useState({ tenant_id: "", channel: "", status: "", q: "", date_from: "", date_to: "" });
  const [detail, setDetail] = useState(null);

  const loadSummary = useCallback(() => {
    api.get("/admin/connect/communications/summary").then((r) => setSummary(r.data)).catch(() => setSummary(null));
  }, []);

  const loadRows = useCallback(() => {
    setRows(null);
    const params = {};
    Object.entries(filters).forEach(([k, v]) => { if (v) params[k] = v; });
    api.get("/admin/connect/communications", { params }).then((r) => setRows(r.data?.items || [])).catch(() => setRows([]));
  }, [filters]);

  useEffect(() => { loadSummary(); }, [loadSummary]);
  useEffect(() => { loadRows(); }, [loadRows]);
  useEffect(() => {
    api.get("/tenants").then((r) => setTenants((r.data?.tenants || r.data || []))).catch(() => setTenants([]));
  }, []);

  const openDetail = (id) => {
    api.get(`/admin/connect/communications/${id}`).then((r) => setDetail(r.data)).catch(() => {});
  };

  const set = (k, v) => setFilters((f) => ({ ...f, [k]: v === "__all" ? "" : v }));

  return (
    <div data-testid="admin-connect-comms">
      <div className="mb-5 border-b border-border pb-4 flex items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Connect Communications</h1>
          <p className="text-sm text-muted-foreground mt-1">Support &amp; billing verification · read-only</p>
        </div>
        <Button variant="outline" size="sm" className="rounded-sm" onClick={() => { loadSummary(); loadRows(); }} data-testid="acc-refresh">
          <ArrowClockwise size={15} weight="bold" className="mr-1.5" /> Refresh
        </Button>
      </div>

      {/* Summary */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-4">
        <Stat icon={PaperPlaneTilt} label="Today" value={summary?.total ?? "—"} />
        <Stat icon={CheckCircle} label="Delivered" value={summary?.delivered ?? "—"} tone="text-emerald-600" />
        <Stat icon={Clock} label="Pending" value={summary?.pending ?? "—"} tone="text-amber-600" />
        <Stat icon={XCircle} label="Failed" value={summary?.failed ?? "—"} tone="text-red-600" />
        <Stat icon={Coins} label="RC Today" value={summary ? rc(summary.rc_consumed_milli) : "—"} tone="text-[#16A34A]" />
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-2 mb-3">
        <div className="relative flex-1 min-w-[220px]">
          <MagnifyingGlass size={15} weight="bold" className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input value={filters.q} onChange={(e) => set("q", e.target.value)} placeholder="Search account, customer or provider message ID"
            className="rounded-sm pl-8" data-testid="acc-search" />
        </div>
        <Select value={filters.tenant_id || "__all"} onValueChange={(v) => set("tenant_id", v)}>
          <SelectTrigger className="rounded-sm w-[170px]" data-testid="acc-filter-tenant"><SelectValue placeholder="Tenant" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="__all">All tenants</SelectItem>
            {tenants.map((t) => <SelectItem key={t.tenant_id} value={t.tenant_id}>{t.company_name || t.tenant_id}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={filters.channel || "__all"} onValueChange={(v) => set("channel", v)}>
          <SelectTrigger className="rounded-sm w-[130px]" data-testid="acc-filter-channel"><SelectValue placeholder="Channel" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="__all">All channels</SelectItem>
            {["email", "sms", "whatsapp", "voice"].map((c) => <SelectItem key={c} value={c} className="capitalize">{c}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={filters.status || "__all"} onValueChange={(v) => set("status", v)}>
          <SelectTrigger className="rounded-sm w-[140px]" data-testid="acc-filter-status"><SelectValue placeholder="Status" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="__all">All statuses</SelectItem>
            {["queued", "sent", "delivered", "opened", "bounced", "failed"].map((s) => <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>)}
          </SelectContent>
        </Select>
        <Input type="date" value={filters.date_from} onChange={(e) => set("date_from", e.target.value)} className="rounded-sm w-[150px]" data-testid="acc-date-from" />
        <Input type="date" value={filters.date_to} onChange={(e) => set("date_to", e.target.value)} className="rounded-sm w-[150px]" data-testid="acc-date-to" />
      </div>

      {/* Table */}
      <div className="border border-border rounded-sm overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Date/Time</TableHead><TableHead>Tenant</TableHead><TableHead>Customer / Account</TableHead>
              <TableHead>Channel</TableHead><TableHead>Provider</TableHead><TableHead>Status</TableHead>
              <TableHead className="text-right">RC</TableHead><TableHead>Provider Msg ID</TableHead><TableHead>Failure</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows === null ? (
              <TableRow><TableCell colSpan={9} className="text-center py-10 text-muted-foreground">Loading…</TableCell></TableRow>
            ) : rows.length === 0 ? (
              <TableRow><TableCell colSpan={9} className="text-center py-10 text-muted-foreground" data-testid="acc-empty">No communications match these filters.</TableCell></TableRow>
            ) : rows.map((r) => (
              <TableRow key={r.id} className="cursor-pointer hover:bg-secondary/50" onClick={() => openDetail(r.id)} data-testid={`acc-row-${r.id}`}>
                <TableCell className="whitespace-nowrap text-xs mono">{fmt(r.created_at)}</TableCell>
                <TableCell className="text-xs">{r.tenant_name || r.tenant_id}</TableCell>
                <TableCell className="text-xs"><div className="font-medium">{r.borrower_name || "—"}</div><div className="text-muted-foreground mono">{r.loan_id}</div></TableCell>
                <TableCell className="text-xs capitalize">{r.channel}</TableCell>
                <TableCell className="text-xs">{r.provider || "—"}</TableCell>
                <TableCell><span className={`text-[11px] font-semibold px-2 py-0.5 rounded-sm capitalize ${STATUS_TONE[r.status] || "bg-secondary"}`}>{r.status}</span></TableCell>
                <TableCell className="text-right text-xs mono">{rc(r.credits_charged_milli || 0)}</TableCell>
                <TableCell className="text-[11px] mono text-muted-foreground max-w-[140px] truncate">{r.provider_message_id || "—"}</TableCell>
                <TableCell className="text-[11px] text-red-600 max-w-[160px] truncate">{r.error || r.bounce_reason || ""}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {/* Detail drawer */}
      <Sheet open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <SheetContent className="w-full sm:max-w-md overflow-y-auto" data-testid="acc-detail">
          <SheetHeader><SheetTitle>Communication detail</SheetTitle></SheetHeader>
          {detail && (
            <dl className="mt-4 space-y-2.5 text-sm">
              {[
                ["Communication ID", detail.id, true],
                ["Tenant", `${detail.tenant_name || ""} (${detail.tenant_id})`],
                ["Account / Ref", `${detail.borrower_name || "—"} · ${detail.loan_id || ""}`],
                ["Channel / Provider", `${detail.channel} · ${detail.provider || "—"}`],
                ["Recipient (masked)", detail.recipient_masked],
                ["Template", detail.template_id || "—"],
                ["Subject", detail.subject || "—"],
                ["Status", detail.status],
                ["Provider Message ID", detail.provider_message_id || "—", true],
                ["Created", fmt(detail.created_at)],
                ["Sent / Updated", fmt(detail.updated_at)],
                ["Last event", fmt(detail.last_event_at)],
                ["Failure / bounce", detail.error || detail.bounce_reason || "—"],
                ["RC charged", rc(detail.credits_charged_milli || 0)],
                ["Provider cost", detail.provider_cost_paise != null ? `₹${(detail.provider_cost_paise / 100).toFixed(2)}` : "—"],
                ["Ledger txn", detail.ledger?.id || "—", true],
                ["Idempotency key", detail.idempotency_key || "—", true],
                ["Credit note", detail.credit_note || "—"],
              ].map(([k, v, monoVal]) => (
                <div key={k} className="grid grid-cols-3 gap-2">
                  <dt className="label-caps col-span-1">{k}</dt>
                  <dd className={`col-span-2 break-words ${monoVal ? "mono text-xs" : ""}`}>{v}</dd>
                </div>
              ))}
              {detail.delivery_events?.length > 0 && (
                <div>
                  <dt className="label-caps mt-3 mb-1">Delivery events</dt>
                  <ul className="space-y-1">
                    {detail.delivery_events.map((e, i) => (
                      <li key={i} className="text-xs flex justify-between"><span className="capitalize">{e.status || e.type}</span><span className="text-muted-foreground mono">{fmt(e.at)}</span></li>
                    ))}
                  </ul>
                </div>
              )}
              <p className="text-[11px] text-muted-foreground pt-3 border-t border-border mt-3">
                Read-only view. Refunds / RC adjustments must use the immutable ledger adjustment flow — historical transactions are never edited.
              </p>
            </dl>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}
